import React from "react";
import { Form, Button } from "react-bootstrap";
import TextInput from "../components/TextInput";
import CommonHeader from "../containers/CommonHeader";
import { PhoneInput } from "../components/NumberInput";
import {
  apiError,
  checkCode,
  errorMessage,
  sendResetCode,
  setPassword as setPasswordRequest,
} from "../services/fidelity";
import useToast from "../hooks/useToast";
import { useDispatch, useSelector } from "react-redux";
import useGoTo from "../hooks/useGoTo";
import useFinishLogin from "../hooks/useFinishLogin";
import { setSendCodeTimeStamp } from "../ducks/userSlice";
import { validatePassword, PASSWORD_MIN_LENGTH } from "../utils/validate";
import phoneDisplay from "../utils/phoneDisplay";
import SendCodeWarning from "../components/SendCodeWarning";
import timeout from "../utils/timeout";

const STEP_CODE = "CODE";
const STEP_PASSWORD = "PASSWORD";

// Serve para o cliente antigo criar a primeira senha e para quem esqueceu a
// senha. São dois passos de propósito: com o código e a senha no mesmo
// formulário, o gerenciador do navegador salva o código como se fosse o
// usuário. No passo da senha o telefone aparece como campo de usuário, que é o
// que o cliente vai digitar no login.
export default function ConfirmationScreen() {
  const dispatch = useDispatch();
  const goTo = useGoTo();
  const toast = useToast();
  const finishLogin = useFinishLogin();
  const { userPhone, userId, sendCodeChannel } = useSelector(
    (state) => state.user,
  );
  const [step, setStep] = React.useState(STEP_CODE);
  const [code, setCode] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [passwordConfirmation, setPasswordConfirmation] = React.useState("");
  const [codeError, setCodeError] = React.useState("");
  const [isLoading, setLoading] = React.useState(false);
  const [showCodeWaring, setShowCodeWarning] = React.useState(true);

  const onChangeCode = (event) => {
    setCode(event.target.value);
    setCodeError("");
  };

  const onChangePassword = (event) => {
    setPassword(event.target.value);
  };

  const onChangePasswordConfirmation = (event) => {
    setPasswordConfirmation(event.target.value);
  };

  const backToCode = (message) => {
    setStep(STEP_CODE);
    setPassword("");
    setPasswordConfirmation("");
    setCodeError(message);
  };

  const confirmCode = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await checkCode(userId, code).then((resp) =>
        resp.json(),
      );

      if (response.error) {
        const failure = apiError(response, "Erro ao conferir o código!");
        if (failure.statusCode === 400) {
          setCodeError("Código inválido. Tente novamente!");
          return;
        }

        throw failure;
      }

      setStep(STEP_PASSWORD);
    } catch (failure) {
      toast.toastError(
        errorMessage(failure, "Erro de conexão. Por favor, tente novamente."),
      );
    } finally {
      setLoading(false);
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await setPasswordRequest(userId, code, password).then(
        (resp) => resp.json(),
      );

      if (response.error) {
        const failure = apiError(response, "Erro ao criar a senha!");
        // o código venceu entre um passo e outro
        if (failure.statusCode === 400) {
          backToCode("Código inválido ou expirado. Peça um novo.");
          return;
        }

        throw failure;
      }

      finishLogin(response);
    } catch (failure) {
      toast.toastError(errorMessage(failure, "Erro ao criar a senha!"));
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await sendResetCode(userPhone).then((resp) =>
        resp.json(),
      );

      if (response.error) {
        throw apiError(response, "Não foi possível reenviar o código.");
      }

      dispatch(setSendCodeTimeStamp({ sendCodeChannel: response.channel }));
    } catch (failure) {
      toast.toastError(
        errorMessage(failure, "Erro no envio do código. Tente novamente."),
      );
    } finally {
      await timeout(1000);
      setLoading(false);
    }
  };

  const passwordError =
    password.length > 0 && !validatePassword(password)
      ? `Senha com no mínimo ${PASSWORD_MIN_LENGTH} caracteres`
      : "";

  const passwordConfirmationError =
    passwordConfirmation.length > 0 && passwordConfirmation !== password
      ? "As senhas não conferem"
      : "";

  const isPasswordDisabled =
    isLoading ||
    !validatePassword(password) ||
    passwordConfirmation !== password;

  const onBack = () => {
    if (step === STEP_PASSWORD) {
      backToCode("");
      return false;
    }

    goTo("/login");
    return false;
  };

  if (step === STEP_PASSWORD) {
    return (
      <>
        <CommonHeader text="Crie sua senha" onBack={onBack} />
        <div className="mt-3 text-center">
          Escolha a senha que você vai usar para entrar
        </div>
        <Form className="mt-4 px-3 px-lg-0" onSubmit={savePassword}>
          <TextInput
            className="mt-3"
            label="Celular"
            value={userPhone}
            inputComponent={PhoneInput}
            readOnly
            inputProps={{ name: "username", autoComplete: "username" }}
          />
          <TextInput
            className="mt-3"
            autoFocus
            type="password"
            label="Nova senha"
            value={password}
            onChange={onChangePassword}
            error={!!passwordError}
            helperText={passwordError}
            inputProps={{ autoComplete: "new-password" }}
            required
            disabled={isLoading}
          />
          <TextInput
            className="mt-3"
            type="password"
            label="Confirme a senha"
            value={passwordConfirmation}
            onChange={onChangePasswordConfirmation}
            error={!!passwordConfirmationError}
            helperText={passwordConfirmationError}
            inputProps={{ autoComplete: "new-password" }}
            required
            disabled={isLoading}
          />
          <div className="form-footer-spacer" aria-hidden="true" />
          <div className="form-footer fixed-bottom">
            <Button
              type="submit"
              className="w-100"
              variant="shop"
              disabled={isPasswordDisabled}
            >
              Salvar senha e entrar
            </Button>
          </div>
        </Form>
      </>
    );
  }

  return (
    <>
      <CommonHeader text="Confirmação" onBack={onBack} />
      <div className="mt-3 text-center">
        Insira o código que foi enviado para
      </div>
      <div className="mt-2 text-center">
        <strong>{`${phoneDisplay(userPhone)} via ${sendCodeChannel}`}</strong>
      </div>
      <Form className="mt-4 px-3 px-lg-0" onSubmit={confirmCode}>
        <TextInput
          autoFocus
          label="Código"
          className="mt-3 otp-input"
          value={code}
          onChange={onChangeCode}
          error={!!codeError}
          helperText={codeError}
          // one-time-code impede o gerenciador de senhas de tratar o código
          // como usuário e habilita o preenchimento automático do SMS no iOS
          inputProps={{
            name: "code",
            autoComplete: "one-time-code",
            inputMode: "numeric",
          }}
          required
          disabled={isLoading}
        />
        <SendCodeWarning
          showWarning={showCodeWaring}
          setShowWarning={setShowCodeWarning}
          resend={
            <ButtonResendCode onClick={resendCode} isLoading={isLoading} />
          }
        />
        <div className="form-footer-spacer" aria-hidden="true" />
        <div className="form-footer fixed-bottom">
          <Button
            type="submit"
            className="w-100"
            variant="shop"
            disabled={isLoading || code.trim().length === 0}
          >
            Continuar
          </Button>
        </div>
      </Form>
    </>
  );
}

function ButtonResendCode({ isLoading, ...rest }) {
  return (
    <Button
      className="w-100 mt-5"
      type="button"
      variant="link"
      disabled={isLoading}
      {...rest}
    >
      Reenviar código
    </Button>
  );
}
